import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

export function useLocalStorageState<T>(
  key: string,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void];

export function useLocalStorageState<T>(
  key: string,
  initialState: T,
): [T, Dispatch<SetStateAction<T>>, () => void];

export function useLocalStorageState<T = undefined>(
  key: string,
  initialState?: T,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] {
  const [value, setValue] = useState(() => {
    let localValue = null;
    if (typeof window !== 'undefined') {
      localValue = window.localStorage.getItem(key);
    }

    if (localValue !== null) {
      try {
        return JSON.parse(localValue) as T;
      } catch (error) {
        return undefined;
      }
    }

    return initialState;
  });

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  const clearValue = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(key);
      setValue(initialState);
    }
  }, [key, initialState]);

  return [value, setValue, clearValue];
}
